import './activity.css';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { queryString } from '../../lib/api-client.js';
import { publicSourcePage } from '../../lib/public-source-page.js';
import { useApiQuery, useUrlFilters } from '../../hooks.js';
import { Button, DataNote, DataTable, FilterPanel, Loading, Pagination, RequestError, SelectField, SourceLink, TextField } from '../../components/ui.js';
import { date, number, percent } from '../../format.js';
import { ProfileIcon } from '../../components/profile-icon.js';

type RecordItem = Record<string, unknown>;

interface ProposalSummary {
  approved?: number;
  in_progress?: number;
  archived?: number;
  withdrawn?: number;
  other?: number;
  total?: number;
}

interface ProposalsResponse {
  items: RecordItem[];
  total?: number;
  page?: number;
  size?: number;
  options?: { types?: string[]; statuses?: string[]; authorships?: string[] };
  summary?: ProposalSummary;
  note?: string;
}

interface VotesResponse {
  items: RecordItem[];
  total?: number;
  page?: number;
  size?: number;
  chart?: Array<{ label: string; value: number }>;
  options?: { positions?: string[]; sessions?: string[]; proposal_types?: string[] };
  note?: string;
  attendance_available?: boolean;
}

interface PeriodProps {
  id: number;
  year: number;
  allYears: boolean;
}

function useProfileFilters(id: number, year: number, allYears: boolean, tab: 'proposals' | 'votes') {
  const { params, setValue } = useUrlFilters();
  const page = Math.max(1, Number(params.get('page') ?? 1) || 1);
  const values = {
    year,
    all_years: allYears,
    page,
    size: 10,
    type: tab === 'proposals' ? params.get('type') ?? '' : undefined,
    status: tab === 'proposals' ? params.get('status') ?? '' : undefined,
    author: tab === 'proposals' ? params.get('author') ?? '' : undefined,
    sort: tab === 'proposals' ? params.get('sort') ?? 'date_desc' : undefined,
    session_name: tab === 'votes' ? params.get('session_name') ?? '' : undefined,
    proposal_type: tab === 'votes' ? params.get('proposal_type') ?? '' : undefined,
    position: tab === 'votes' ? params.get('position') ?? '' : undefined,
    q: params.get('q') ?? '',
  };
  const query = queryString(values);
  const resource = tab === 'proposals' ? 'proposals' : 'votes';
  const data = useApiQuery<ProposalsResponse | VotesResponse>(
    [`profile-${resource}`, id, query],
    `/api/politicians/${id}/${resource}?${query}`,
  );
  const update = (key: string, value: string) => setValue(key, value);
  const changePage = (nextPage: number) => setValue('page', String(nextPage), { resetPage: false });
  return { values, data, update, changePage };
}

function FilterSearch({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (value: string) => void; placeholder: string }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (draft === value) return;
    const timer = window.setTimeout(() => onChange(draft), 300);
    return () => window.clearTimeout(timer);
  }, [draft, value, onChange]);
  return <TextField label={label} type="search" value={draft} onValueChange={setDraft} placeholder={placeholder} />;
}

function ProfileFilters({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <FilterPanel className={`profile-activity-filters ${className}`.trim()} ariaLabel="Filtros">
    <div className="profile-activity-filter-grid">{children}</div>
  </FilterPanel>;
}

function FilterSelect({ label, value, options, onChange, allLabel }: { label: string; value: string; options: string[]; onChange: (value: string) => void; allLabel: string }) {
  return <SelectField label={label} value={value} onValueChange={onChange}>
    <option value="">{allLabel}</option>
    {options.filter(Boolean).map((option) => <option value={option} key={option}>{option}</option>)}
  </SelectField>;
}

function ActivityHeading({ title, description, icon }: { title: string; description: string; icon: 'proposal-document' | 'parliament' }) {
  return <header className="profile-activity-heading">
    <span className="profile-activity-heading-icon"><ProfileIcon name={icon} size={25} /></span>
    <div><h2>{title}</h2><p>{description}</p></div>
  </header>;
}

function SortSelect({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return <SelectField label="Ordenar por" value={value} onValueChange={onChange}>
    <option value="date_desc">Mais recentes</option><option value="date_asc">Mais antigas</option>
    <option value="title">Título</option><option value="status">Situação</option>
  </SelectField>;
}

function SummaryCard({ label, value, total, emphasize = false }: { label: string; value: number; total?: number; emphasize?: boolean }) {
  const share = total && total > 0 ? percent(value / total * 100) : null;
  return <article className={`profile-activity-summary-card${emphasize ? ' is-total' : ''}`}>
    <strong>{number(value)}</strong><span>{label}</span>{share && <small>{share}</small>}
  </article>;
}

function ProposalSummary({ summary }: { summary: ProposalSummary | undefined }) {
  const total = Number(summary?.total ?? 0);
  const cards = [
    ['Aprovadas', Number(summary?.approved ?? 0)],
    ['Em tramitação', Number(summary?.in_progress ?? 0)],
    ['Arquivadas', Number(summary?.archived ?? 0)],
    ['Retiradas', Number(summary?.withdrawn ?? 0)],
    ['Outras', Number(summary?.other ?? 0)],
  ] as const;
  return <section className="profile-activity-summary profile-proposal-summary" aria-label="Resumo das proposições">
    {cards.map(([label, value]) => <SummaryCard key={label} label={label} value={value} total={total} />)}
    <SummaryCard label="Total de proposições" value={total} emphasize />
  </section>;
}

function VoteSummary({ chart = [], total }: { chart?: Array<{ label: string; value: number }>; total: number }) {
  const rank = ['AP', 'LP', 'NCom', 'Não', 'P-NRV', 'Sim', 'Voto secreto — participação registrada'];
  const items = [...chart].sort((a, b) => {
    const ai = rank.indexOf(a.label);
    const bi = rank.indexOf(b.label);
    return (ai < 0 ? rank.length : ai) - (bi < 0 ? rank.length : bi) || a.label.localeCompare(b.label, 'pt-BR');
  });
  return <section className="profile-activity-summary profile-vote-summary" aria-label="Resumo das votações nominais">
    <article className="profile-activity-summary-card profile-vote-total-card"><strong>{number(total)}</strong><span>Votações nominais registradas</span><small>Fonte oficial · registros do período</small></article>
    {items.map(({ label, value }) => <SummaryCard key={label} label={label} value={value} total={total} />)}
  </section>;
}

function safeText(value: unknown, fallback = 'Não informado') {
  if (typeof value !== 'string' && typeof value !== 'number') return fallback;
  const text = String(value).trim();
  return text || fallback;
}

function dateForTable(value: unknown) {
  if (!value) return 'Não informada';
  const text = String(value);
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : date(text);
}

function ProposalTitle({ title, summary }: { title: string; summary: string }) {
  const descriptionId = useId();
  const hideTimerRef = useRef<number | null>(null);
  const [pointerActive, setPointerActive] = useState(false);
  const [focusActive, setFocusActive] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const hasDescription = summary.length > 0 && summary !== title;
  const isOpen = hasDescription && (pointerActive || focusActive) && !dismissed;

  useEffect(() => () => {
    if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
  }, []);

  if (!hasDescription) return <span className="profile-proposal-title">{title}</span>;

  const cancelHide = () => {
    if (hideTimerRef.current !== null) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  };
  const schedulePointerHide = () => {
    cancelHide();
    hideTimerRef.current = window.setTimeout(() => setPointerActive(false), 140);
  };
  const tooltip = isOpen && typeof document !== 'undefined' ? createPortal(
    <div
      id={`${descriptionId}-tooltip`}
      className="profile-proposal-tooltip"
      role="tooltip"
      aria-hidden="true"
      onPointerEnter={cancelHide}
      onPointerLeave={schedulePointerHide}
    >
      {summary}
    </div>,
    document.body,
  ) : null;

  return <>
    <Button
      variant="unstyled"
      type="button"
      className={`profile-proposal-title profile-proposal-title-trigger${isOpen ? ' is-tooltip-anchor' : ''}`}
      aria-describedby={descriptionId}
      onPointerEnter={() => { cancelHide(); setDismissed(false); setPointerActive(true); }}
      onPointerLeave={schedulePointerHide}
      onFocus={() => { setDismissed(false); setFocusActive(true); }}
      onBlur={() => setFocusActive(false)}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setDismissed(true);
          event.currentTarget.blur();
        }
      }}
    >
      {title}
    </Button>
    <span id={descriptionId} className="profile-proposal-description-accessible">{summary}</span>
    {tooltip}
  </>;
}

function ProposalTable({ items }: { items: RecordItem[] }) {
  if (!items.length) return <p className="empty-inline">Nenhuma proposição encontrada para os filtros selecionados. A ausência de registros não significa ausência de atividade.</p>;
  const records = items.map((item, index) => ({
    key: String(item.id ?? item.external_id ?? index),
    id: safeText(item.external_id, 'Registro'),
    title: safeText(item.title),
    summary: safeText(item.summary, ''),
    type: safeText(item.proposal_type),
    status: safeText(item.status),
    authorship: safeText(item.authorship),
    date: dateForTable(item.date),
    sourceUrl: typeof item.source_url === 'string' ? item.source_url : null,
  }));
  return <>
    <DataTable className="profile-activity-table-scroll profile-proposal-table-scroll" tableClassName="profile-proposal-table">
        <thead><tr><th>Proposição</th><th>Título</th><th>Tipo</th><th>Status</th><th>Atuação</th><th>Data</th><th>Fonte</th></tr></thead>
        <tbody>{records.map((record) => <tr key={record.key}>
          <td>{record.sourceUrl ? <a href={publicSourcePage(record.sourceUrl) ?? undefined} target="_blank" rel="noreferrer">{record.id} ↗</a> : record.id}</td>
          <td><ProposalTitle title={record.title} summary={record.summary} /></td>
          <td>{record.type}</td>
          <td><span className="profile-source-status">{record.status}</span></td>
          <td>{record.authorship}</td>
          <td>{record.date}</td>
          <td><SourceLink href={record.sourceUrl} /></td>
        </tr>)}</tbody>
    </DataTable>
    <div className="profile-proposal-cards" aria-label="Lista de proposições">
      {records.map((record) => <article className="profile-proposal-card" key={record.key}>
        <header>
          <ProposalTitle title={record.title} summary={record.summary} />
          <span className="profile-source-status">{record.status}</span>
        </header>
        <dl>
          <div><dt>Proposição</dt><dd>{record.sourceUrl ? <a href={publicSourcePage(record.sourceUrl) ?? undefined} target="_blank" rel="noreferrer">{record.id} ↗</a> : record.id}</dd></div>
          <div><dt>Tipo</dt><dd>{record.type}</dd></div>
          <div><dt>Atuação</dt><dd>{record.authorship}</dd></div>
          <div><dt>Data</dt><dd>{record.date}</dd></div>
          <div><dt>Fonte</dt><dd><SourceLink href={record.sourceUrl} /></dd></div>
        </dl>
      </article>)}
    </div>
  </>;
}

export function ProposalsTab({ id, year, allYears }: PeriodProps) {
  const { values, data, update, changePage } = useProfileFilters(id, year, allYears, 'proposals');
  const result = data.data as ProposalsResponse | undefined;
  const options = result?.options;
  if (data.isLoading) return <Loading />;
  if (data.error) return <RequestError message={data.error.message} />;
  if (!result) return null;
  const summary = result.summary;
  return <div className="profile-content profile-activity-page profile-proposals-page">
    <ActivityHeading title="Proposições" icon="proposal-document" description="Projetos, requerimentos e outras proposições vinculadas ao perfil nas fontes oficiais." />
    <ProfileFilters className="profile-proposal-filters">
      <FilterSelect label="Tipo de proposição" value={String(values.type ?? '')} options={options?.types ?? []} onChange={(value) => update('type', value)} allLabel="Todos os tipos" />
      <FilterSelect label="Situação" value={String(values.status ?? '')} options={options?.statuses ?? []} onChange={(value) => update('status', value)} allLabel="Todos os status" />
      <FilterSelect label="Autoria/relatoria" value={String(values.author ?? '')} options={options?.authorships ?? []} onChange={(value) => update('author', value)} allLabel="Todas" />
      <FilterSearch label="Buscar título, ementa ou número" value={String(values.q)} onChange={(value) => update('q', value)} placeholder="Ex.: PL 123 ou educação" />
      <SortSelect value={String(values.sort)} onChange={(value) => update('sort', value)} />
    </ProfileFilters>
    <ProposalSummary summary={summary} />
    <DataNote>O status pode mudar ao longo da tramitação. “Aprovada” indica uma etapa descrita pela fonte, não confirma sanção ou transformação em lei. O resumo abrange o período selecionado e não muda com os filtros da tabela.</DataNote>
    <section className="panel profile-activity-records">
      <header className="profile-activity-section-heading"><h2>Proposições</h2><span>{number(result.total ?? 0)} registros conforme os filtros selecionados</span></header>
      <ProposalTable items={result.items ?? []} />
      <Pagination page={Number(result.page ?? 1)} size={Number(result.size ?? 10)} total={Number(result.total ?? 0)} onChange={changePage} />
    </section>
  </div>;
}

function VoteGroupTable({ items }: { items: RecordItem[] }) {
  if (!items.length) return <p className="empty-inline">Nenhum voto nominal encontrado para os filtros selecionados. A ausência de registro não informa presença ou ausência na sessão.</p>;
  const bySession = new Map<string, { date: unknown; session: string; items: RecordItem[] }>();
  for (const item of items) {
    const session = safeText(item.session, 'Sessão não informada');
    const groupKey = `${String(item.date ?? '')}|${session}`;
    const group = bySession.get(groupKey) ?? { date: item.date, session, items: [] };
    group.items.push(item);
    bySession.set(groupKey, group);
  }
  const groups = [...bySession.values()];
  return <div className="profile-vote-groups">{groups.map((group) => <section className="panel profile-vote-session" key={`${String(group.date ?? '')}|${group.session}`}>
    <header className="profile-vote-session-heading"><strong>{dateForTable(group.date)}</strong><span>{group.session}</span><small>{group.items.length} {group.items.length === 1 ? 'votação' : 'votações'}</small></header>
    <DataTable className="profile-activity-table-scroll" tableClassName="profile-vote-table">
      <thead><tr><th>Proposição</th><th>Tipo</th><th>Resultado oficial</th><th>Voto do parlamentar</th><th>Fonte</th></tr></thead>
      <tbody>{group.items.map((item, index) => {
        const matter = safeText(item.proposal_summary, safeText(item.description));
        const label = safeText(item.matter_label, safeText(item.matter_number, 'Matéria sem identificação'));
        const secret = safeText(item.position).toLocaleLowerCase('pt-BR').includes('secreto');
        return <tr key={`${String(item.source_url ?? '')}-${index}`}>
          <td><span className="profile-vote-matter-label">{label}</span><span className="profile-vote-matter-summary">{matter}</span></td>
          <td>{safeText(item.proposal_type)}</td>
          <td>{safeText(item.outcome)}</td>
          <td><span className={`profile-vote-position${secret ? ' is-secret' : ''}`}>{safeText(item.position)}</span></td>
          <td><SourceLink href={typeof item.source_url === 'string' ? item.source_url : null}>{label}</SourceLink></td>
        </tr>;
      })}</tbody>
    </DataTable>
  </section>)}</div>;
}

export function VotesTab({ id, year, allYears }: PeriodProps) {
  const { values, data, update, changePage } = useProfileFilters(id, year, allYears, 'votes');
  const result = data.data as VotesResponse | undefined;
  const options = result?.options;
  if (data.isLoading) return <Loading />;
  if (data.error) return <RequestError message={data.error.message} />;
  if (!result) return null;
  const positions = result.chart ?? [];
  return <div className="profile-content profile-activity-page profile-votes-page">
    <ActivityHeading title="Votações nominais" icon="parliament" description="Registros de posicionamentos individuais em votações nominais das fontes oficiais." />
    <ProfileFilters className="profile-vote-filters">
      <FilterSelect label="Sessão" value={String(values.session_name ?? '')} options={options?.sessions ?? []} onChange={(value) => update('session_name', value)} allLabel="Todas as sessões" />
      <FilterSelect label="Tipo de proposição" value={String(values.proposal_type ?? '')} options={options?.proposal_types ?? []} onChange={(value) => update('proposal_type', value)} allLabel="Todos os tipos" />
      <FilterSearch label="Buscar matéria" value={String(values.q)} onChange={(value) => update('q', value)} placeholder="Número ou assunto" />
      <FilterSelect label="Voto do parlamentar" value={String(values.position ?? '')} options={options?.positions ?? []} onChange={(value) => update('position', value)} allLabel="Todos os votos" />
    </ProfileFilters>
    <VoteSummary chart={positions} total={Number(result.total ?? 0)} />
    <DataNote>Votos nominais mostram o posicionamento nas votações registradas; não informam presença em todas as sessões. “Voto secreto — participação registrada” não revela posição individual. A posição partidária não é presumida.{result.attendance_available ? '' : ' Não há indicador de presença disponível neste conjunto.'}</DataNote>
    <section className="profile-vote-results" aria-label="Votações agrupadas por sessão e data">
      <VoteGroupTable items={result.items ?? []} />
      <Pagination page={Number(result.page ?? 1)} size={Number(result.size ?? 10)} total={Number(result.total ?? 0)} onChange={changePage} />
    </section>
  </div>;
}
